import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Users, Calendar, CreditCard, TrendingUp, 
  Clock, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, trend, color = 'emerald', isDarkMode }) => {
  const colorClasses = {
    emerald: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600',
    blue: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600',
    amber: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600',
    rose: isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-600'
  };

  return (
    <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{title}</p>
            <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
            {trend && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                <TrendingUp className="h-3 w-3" />
                {trend}
              </p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const { admin, token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [stats, setStats] = useState({
    totalUsers: 0,
    todayBookings: 0,
    totalRevenue: 0,
    pendingTickets: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch stats
        const [usersRes, bookingsRes] = await Promise.all([
          fetch(`${API}/admin/users`, { 
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API}/admin/bookings?limit=10`, { 
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setStats(prev => ({ ...prev, totalUsers: usersData.total || 0 }));
        }

        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          setRecentBookings(bookingsData.bookings || []);
          
          // Calculate today's bookings
          const now = new Date();
          const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          const todayCount = (bookingsData.bookings || []).filter(b => b.date === today).length;
          setStats(prev => ({ ...prev, todayBookings: todayCount, totalRevenue: bookingsData.total || 0 }));
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { 
        icon: CheckCircle, 
        class: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700' 
      },
      pending: { 
        icon: Clock, 
        class: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700' 
      },
      cancelled: { 
        icon: XCircle, 
        class: isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700' 
      },
      no_show: { 
        icon: AlertCircle, 
        class: isDarkMode ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-700' 
      }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.class}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className={`text-2xl font-bold ${theme.text}`}>Dashboard</h1>
          <p className={`${theme.textMuted} mt-1`}>Welcome back, {admin?.name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={Users}
            color="blue"
            isDarkMode={isDarkMode}
          />
          <StatCard
            title="Today's Bookings"
            value={stats.todayBookings}
            icon={Calendar}
            color="emerald"
            isDarkMode={isDarkMode}
          />
          <StatCard
            title="Total Bookings"
            value={stats.totalRevenue}
            icon={CreditCard}
            color="amber"
            isDarkMode={isDarkMode}
          />
          <StatCard
            title="Pending Support"
            value={stats.pendingTickets}
            icon={AlertCircle}
            color="rose"
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Recent Bookings */}
        <Card className={isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`text-lg ${theme.text}`}>Recent Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
              </div>
            ) : recentBookings.length === 0 ? (
              <p className={`text-center py-8 ${theme.textMuted}`}>No bookings yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm border-b ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
                      <th className="pb-3 font-medium">Booking ID</th>
                      <th className="pb-3 font-medium">Customer</th>
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Time</th>
                      <th className="pb-3 font-medium">Court</th>
                      <th className="pb-3 font-medium">Amount</th>
                      <th className="pb-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {recentBookings.slice(0, 10).map((booking) => (
                      <tr key={booking.booking_id} className={`border-b ${isDarkMode ? 'border-slate-700/50 text-slate-300' : 'border-gray-100 text-gray-700'}`}>
                        <td className={`py-3 font-mono text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{booking.booking_id.slice(-8)}</td>
                        <td className={`py-3 ${theme.text}`}>{booking.user?.name || 'Unknown'}</td>
                        <td className={`py-3 ${theme.text}`}>{booking.date}</td>
                        <td className={`py-3 ${theme.text}`}>{booking.start_time} - {booking.end_time}</td>
                        <td className={`py-3 ${theme.text}`}>{booking.court_id}</td>
                        <td className={`py-3 ${theme.text}`}>₱{booking.total_amount?.toFixed(2)}</td>
                        <td className="py-3">{getStatusBadge(booking.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className={`cursor-pointer transition-colors ${
              isDarkMode 
                ? 'bg-slate-800/50 border-slate-700 hover:border-emerald-500/50' 
                : 'bg-white border-gray-200 hover:border-emerald-500 shadow-sm hover:shadow-md'
            }`}
            onClick={() => window.location.href = '/admin/bookings'}
          >
            <CardContent className="p-6 text-center">
              <Calendar className={`h-8 w-8 mx-auto mb-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <p className={`font-medium ${theme.text}`}>Manage Bookings</p>
              <p className={`text-sm ${theme.textMuted}`}>View and edit reservations</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-colors ${
              isDarkMode 
                ? 'bg-slate-800/50 border-slate-700 hover:border-emerald-500/50' 
                : 'bg-white border-gray-200 hover:border-blue-500 shadow-sm hover:shadow-md'
            }`}
            onClick={() => window.location.href = '/admin/pos'}
          >
            <CardContent className="p-6 text-center">
              <CreditCard className={`h-8 w-8 mx-auto mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <p className={`font-medium ${theme.text}`}>POS System</p>
              <p className={`text-sm ${theme.textMuted}`}>Process walk-in sales</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-colors ${
              isDarkMode 
                ? 'bg-slate-800/50 border-slate-700 hover:border-emerald-500/50' 
                : 'bg-white border-gray-200 hover:border-amber-500 shadow-sm hover:shadow-md'
            }`}
            onClick={() => window.location.href = '/admin/support'}
          >
            <CardContent className="p-6 text-center">
              <AlertCircle className={`h-8 w-8 mx-auto mb-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              <p className={`font-medium ${theme.text}`}>Support Tickets</p>
              <p className={`text-sm ${theme.textMuted}`}>Handle customer issues</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
